import { useState, useEffect, useCallback, useRef } from 'react'
import type { AddressResult, OrderProgress as OrderProgressType, DriverInfo, GeoPoint } from '../types'
import { getOrderProgress, cancelOrder, submitFeedback } from '../services/api'

type OrderStatus = 'search' | 'driving' | 'waiting' | 'transporting' | 'complete' | 'cancelled' | 'failed'

const STATUS_INFO: Record<OrderStatus, { title: string; subtitle: string; icon: string }> = {
  search: { title: 'Ищем машину', subtitle: 'Это займёт пару минут', icon: '🔍' },
  driving: { title: 'Водитель едет к вам', subtitle: 'Машина в пути', icon: '🚕' },
  waiting: { title: 'Машина ожидает', subtitle: 'Водитель на месте', icon: '⏰' },
  transporting: { title: 'В пути', subtitle: 'Приятной поездки!', icon: '🛣️' },
  complete: { title: 'Поездка завершена', subtitle: 'Спасибо за заказ!', icon: '✅' },
  cancelled: { title: 'Заказ отменён', subtitle: 'Вы отменили поездку', icon: '❌' },
  failed: { title: 'Ошибка заказа', subtitle: 'Не удалось найти машину', icon: '⚠️' },
}

const TARIFF_NAMES: Record<string, string> = {
  econom: 'Эконом',
  comfort: 'Комфорт',
  comfortplus: 'Комфорт+',
  business: 'Бизнес',
  vip: 'VIP',
  ultimate: 'Ultimate',
  maybach: 'Maybach',
  child_tariff: 'Детский',
  lite_b2b: 'Лайт',
  standart_b2b: 'Стандарт',
  optimum_b2b: 'Оптимум',
  premium_van: 'Премиум Ван',
  minivan: 'Минивэн',
}

interface OrderScreenProps {
  orderId: string
  fromAddress?: AddressResult | null
  toAddress?: AddressResult | null
  tariff?: string
  price?: number
  priceUsdt?: number
  onNewOrder: () => void
}

const DEFAULT_ADDRESS: AddressResult = { display_name: '', lat: 55.7558, lon: 37.6173 }

export const OrderScreen = ({ orderId, fromAddress: fromAddressProp, toAddress: toAddressProp, tariff: tariffProp = '', price: priceProp = 0, priceUsdt = 0, onNewOrder }: OrderScreenProps) => {
  const [fromAddress, setFromAddress] = useState<AddressResult>(fromAddressProp || DEFAULT_ADDRESS)
  const [toAddress, setToAddress] = useState<AddressResult>(toAddressProp || DEFAULT_ADDRESS)
  const [tariff, setTariff] = useState(tariffProp)
  const [price, setPrice] = useState(priceProp)
  const [status, setStatus] = useState<OrderStatus>('search')
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [driverInfo, setDriverInfo] = useState<DriverInfo | null>(null)
  const [vehicleLocation, setVehicleLocation] = useState<GeoPoint | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Rating state
  const [rating, setRating] = useState<number>(0)
  const [feedbackMsg, setFeedbackMsg] = useState('')
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  // Map refs
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const driverMarkerRef = useRef<L.Marker | null>(null)
  const fromMarkerRef = useRef<L.Marker | null>(null)
  const toMarkerRef = useRef<L.Marker | null>(null)
  const routeLineRef = useRef<L.Polyline | null>(null)

  // Initialize map (once)
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return
    const L = window.L
    if (!L) return

    const map = L.map(mapRef.current, {
      center: [55.7558, 37.6173],
      zoom: 11,
      zoomControl: false,
      attributionControl: false,
    })

    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
    }).addTo(map)

    mapInstanceRef.current = map

    return () => {
      map.remove()
      mapInstanceRef.current = null
    }
  }, [])

  // Update from/to markers when addresses change
  useEffect(() => {
    if (!mapInstanceRef.current) return
    const L = window.L
    if (!L) return
    const map = mapInstanceRef.current

    const hasFrom = fromAddress.display_name !== ''
    const hasTo = toAddress.display_name !== ''

    // Clear old markers
    if (fromMarkerRef.current) { fromMarkerRef.current.remove(); fromMarkerRef.current = null }
    if (toMarkerRef.current) { toMarkerRef.current.remove(); toMarkerRef.current = null }
    if (routeLineRef.current) { routeLineRef.current.remove(); routeLineRef.current = null }

    if (hasFrom) {
      const fromIcon = L.divIcon({
        className: 'custom-marker-from',
        html: `
          <div style="
            width: 32px; height: 32px;
            background: linear-gradient(135deg, #1DCC98, #139F67);
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            box-shadow: 0 4px 12px rgba(29, 204, 152, 0.5);
            border: 2px solid rgba(255,255,255,0.9);
          ">
            <div style="width: 10px; height: 10px; background: white; border-radius: 50%;"></div>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      })
      fromMarkerRef.current = L.marker([fromAddress.lat, fromAddress.lon], { icon: fromIcon }).addTo(map)
    }

    if (hasTo) {
      const toIcon = L.divIcon({
        className: 'custom-marker-to',
        html: `
          <div style="
            width: 32px; height: 32px;
            background: linear-gradient(135deg, #9362CC, #7043B8);
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            box-shadow: 0 4px 12px rgba(147, 98, 204, 0.5);
            border: 2px solid rgba(255,255,255,0.9);
          ">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
              <circle cx="12" cy="10" r="3" fill="white"/>
            </svg>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      })
      toMarkerRef.current = L.marker([toAddress.lat, toAddress.lon], { icon: toIcon }).addTo(map)
    }

    // Route line between points
    if (hasFrom && hasTo) {
      routeLineRef.current = L.polyline(
        [[fromAddress.lat, fromAddress.lon], [toAddress.lat, toAddress.lon]],
        {
          color: '#9362CC',
          weight: 4,
          opacity: 0.7,
          dashArray: '8, 12',
          lineCap: 'round',
        }
      ).addTo(map)

      const bounds = L.latLngBounds([
        [fromAddress.lat, fromAddress.lon],
        [toAddress.lat, toAddress.lon],
      ])
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 })
    } else if (hasFrom) {
      map.setView([fromAddress.lat, fromAddress.lon], 14)
    } else if (hasTo) {
      map.setView([toAddress.lat, toAddress.lon], 14)
    }
  }, [fromAddress, toAddress])

  // Update driver marker on map
  useEffect(() => {
    if (!mapInstanceRef.current || !vehicleLocation) return
    const L = window.L
    if (!L) return
    const map = mapInstanceRef.current

    const driverIcon = L.divIcon({
      className: 'custom-marker-driver',
      html: `
        <div style="position: relative;">
          <div style="
            width: 44px; height: 44px;
            background: linear-gradient(135deg, #FEDB99, #FF5869);
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            box-shadow: 0 4px 16px rgba(255, 88, 105, 0.5);
            border: 3px solid white;
            animation: carDrive 1s ease-in-out infinite;
          ">
            <span style="font-size: 22px;">🚕</span>
          </div>
          <div style="
            position: absolute; bottom: -8px; left: 50%;
            transform: translateX(-50%);
            width: 20px; height: 6px;
            background: rgba(0,0,0,0.3);
            border-radius: 50%;
            filter: blur(2px);
          "></div>
        </div>
      `,
      iconSize: [44, 52],
      iconAnchor: [22, 26],
    })

    if (driverMarkerRef.current) {
      // Smoothly animate to new position
      driverMarkerRef.current.setLatLng([vehicleLocation.lat, vehicleLocation.lon])
    } else {
      driverMarkerRef.current = L.marker(
        [vehicleLocation.lat, vehicleLocation.lon],
        { icon: driverIcon, zIndexOffset: 1000 }
      ).addTo(map)
    }

    // Fit bounds to include driver
    const points: [number, number][] = [
      [vehicleLocation.lat, vehicleLocation.lon],
      [fromAddress.lat, fromAddress.lon],
    ]
    if (status === 'transporting') {
      points.push([toAddress.lat, toAddress.lon])
    }
    const bounds = L.latLngBounds(points)
    map.fitBounds(bounds, { padding: [80, 80], maxZoom: 16, animate: true })
  }, [vehicleLocation, status, fromAddress, toAddress])

  // Polling for order progress
  const fetchProgress = useCallback(async () => {
    try {
      const progress = await getOrderProgress(orderId)
      setStatus(progress.status)
      setTimeLeft(progress.time_left_raw || null)
      if (progress.driver_info) {
        setDriverInfo(progress.driver_info)
      }
      if (progress.vehicle_location) {
        setVehicleLocation(progress.vehicle_location)
      }
      // Update route from API if we don't have addresses
      if (progress.route_points && progress.route_points.length >= 2) {
        const rp = progress.route_points
        const first = rp[0]
        const last = rp[rp.length - 1]
        if (first.geopoint && (!fromAddressProp || !fromAddressProp.display_name)) {
          setFromAddress({
            display_name: first.fullname || 'Откуда',
            lon: first.geopoint[0],
            lat: first.geopoint[1],
          })
        }
        if (last.geopoint && (!toAddressProp || !toAddressProp.display_name)) {
          setToAddress({
            display_name: last.fullname || 'Куда',
            lon: last.geopoint[0],
            lat: last.geopoint[1],
          })
        }
      }
      // Update tariff and cost from API
      if (progress.tariff && !tariffProp) {
        setTariff(progress.tariff)
      }
      if (progress.cost && !priceProp) {
        setPrice(progress.cost)
      }
    } catch (err) {
      console.error('Failed to fetch order progress:', err)
    }
  }, [orderId, fromAddressProp, toAddressProp, tariffProp, priceProp])

  // Start polling
  useEffect(() => {
    fetchProgress()
    const interval = setInterval(fetchProgress, 3000)
    return () => clearInterval(interval)
  }, [fetchProgress])

  // Handle order cancellation
  const handleCancel = async () => {
    if (isCancelling) return
    setIsCancelling(true)
    setError(null)

    try {
      const result = await cancelOrder(orderId)
      if (result.status === 'cancelled' || result.status === 'already_cancelled') {
        setStatus('cancelled')
      } else if (result.status === 'error' || result.status === 'conflict') {
        setError(result.message || 'Не удалось отменить заказ')
      } else if (result.status === 'not_found') {
        setError('Заказ не найден')
      } else if (result.status?.startsWith('already_')) {
        const finalStatus = result.status.replace('already_', '') as OrderStatus
        setStatus(finalStatus)
      } else {
        setStatus('cancelled')
      }
    } catch (err: any) {
      console.error('Failed to cancel order:', err)
      setError(err?.response?.data?.detail || err?.message || 'Не удалось отменить заказ')
    } finally {
      setIsCancelling(false)
    }
  }

  // Handle feedback submission
  const handleSubmitFeedback = async () => {
    if (rating === 0 || isSubmittingFeedback || feedbackSubmitted) return
    setIsSubmittingFeedback(true)

    try {
      await submitFeedback(orderId, rating, feedbackMsg || undefined)
      setFeedbackSubmitted(true)
    } catch (err: any) {
      console.error('Failed to submit feedback:', err)
      setError(err?.response?.data?.detail || err?.message || 'Failed to submit feedback')
    } finally {
      setIsSubmittingFeedback(false)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const statusInfo = STATUS_INFO[status] || STATUS_INFO.search
  const isActive = !['complete', 'cancelled', 'failed'].includes(status)
  const canCancel = ['search', 'driving', 'waiting'].includes(status)
  const showMap = isActive || status === 'complete'

  return (
    <div className="h-full flex flex-col bg-dark">
      {/* Map area */}
      {showMap && (
        <div className="relative flex-1 min-h-[200px] overflow-hidden">
          <div ref={mapRef} className="h-full w-full" style={{ zIndex: 0 }} />

          {/* Gradient overlays */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-dark-tertiary via-dark-tertiary/80 to-transparent pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-dark/90 to-transparent pointer-events-none" />

          {/* Status badge over map */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
            <div className={`
              flex items-center gap-2 px-4 py-2 rounded-2xl backdrop-blur-md border
              ${isActive
                ? 'bg-dark/70 border-primary/30 shadow-purple'
                : 'bg-dark/70 border-white/10'
              }
            `}>
              {isActive && (
                <div className="w-2 h-2 rounded-full bg-accent-green animate-pulse" />
              )}
              <span className="text-sm font-semibold text-white">{statusInfo.title}</span>
              {isActive && timeLeft !== null && (
                <span className="text-sm font-bold text-primary">{formatTime(timeLeft)}</span>
              )}
            </div>
          </div>

          {/* Order ID badge */}
          <div className="absolute top-4 right-4 z-10">
            <div className="px-3 py-1.5 rounded-xl bg-dark/70 backdrop-blur-md border border-white/10">
              <p className="text-text-muted text-[10px]">{TARIFF_NAMES[tariff] || tariff}</p>
              <p className="text-primary font-bold text-sm">{price} ₽</p>
            </div>
          </div>

          {/* Center on route button */}
          <button
            className="absolute right-4 bottom-36 w-10 h-10 rounded-xl bg-dark-secondary/90 backdrop-blur border border-primary/10 flex items-center justify-center text-text-secondary hover:text-primary-light transition-all shadow-lg z-10"
            onClick={() => {
              if (mapInstanceRef.current) {
                const L = window.L
                if (L) {
                  const points: [number, number][] = [
                    [fromAddress.lat, fromAddress.lon],
                    [toAddress.lat, toAddress.lon],
                  ]
                  if (vehicleLocation) {
                    points.push([vehicleLocation.lat, vehicleLocation.lon])
                  }
                  const bounds = L.latLngBounds(points)
                  mapInstanceRef.current.fitBounds(bounds, { padding: [60, 60] })
                }
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
            </svg>
          </button>
        </div>
      )}

      {/* No map fallback for cancelled/failed */}
      {!showMap && (
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-24 h-24 rounded-full bg-dark-tertiary flex items-center justify-center mb-6">
            <span className="text-5xl">{statusInfo.icon}</span>
          </div>
          <h2 className="text-2xl font-bold text-white text-center mb-2">{statusInfo.title}</h2>
          <p className="text-text-secondary text-center">{statusInfo.subtitle}</p>
        </div>
      )}

      {/* Bottom panel */}
      <div className="bg-dark-tertiary rounded-t-3xl border-t border-primary/10 relative z-20">
        {/* Handle */}
        <div className="flex justify-center py-2">
          <div className="w-10 h-1 rounded-full bg-text-muted/30" />
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mb-3 bg-accent-red/10 rounded-xl px-4 py-2 border border-accent-red/20">
            <p className="text-accent-red text-sm">{error}</p>
          </div>
        )}

        {/* Driver info */}
        {driverInfo && ['driving', 'waiting', 'transporting'].includes(status) && (
          <div className="mx-4 mb-3 flex items-center gap-3 p-3 bg-dark-secondary rounded-2xl border border-primary/10">
            <div className="w-12 h-12 rounded-full bg-gradient-purple flex items-center justify-center overflow-hidden flex-shrink-0">
              {driverInfo.photo_url ? (
                <img src={driverInfo.photo_url} alt="Driver" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xl">👨</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">{driverInfo.name || 'Водитель'}</p>
              <p className="text-text-muted text-xs truncate">
                {driverInfo.car_model || 'Авто'}
                {driverInfo.car_color && ` · ${driverInfo.car_color}`}
                {driverInfo.car_number && ` · ${driverInfo.car_number}`}
              </p>
              {driverInfo.rating && (
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-amber-400 text-xs">★</span>
                  <span className="text-text-secondary text-xs">{driverInfo.rating.toFixed(2)}</span>
                </div>
              )}
            </div>
            {/* Кнопки связи с водителем */}
            <div className="flex gap-2 flex-shrink-0">
              {driverInfo.phone && (
                <>
                  {/* SMS */}
                  <a
                    href={`sms:${driverInfo.phone}`}
                    className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9362CC" strokeWidth="2">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                    </svg>
                  </a>
                  {/* Звонок */}
                  <a
                    href={`tel:${driverInfo.phone}`}
                    className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1DCC98" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                  </a>
                </>
              )}
            </div>
          </div>
        )}

        {/* Route summary */}
        <div className="mx-4 mb-3 flex items-center gap-3 p-3 bg-dark-secondary rounded-2xl border border-primary/10">
          <div className="flex flex-col items-center py-0.5">
            <div className="w-2 h-2 rounded-full bg-accent-green" />
            <div className="w-0.5 flex-1 bg-text-muted/30 my-1" />
            <div className="w-2 h-2 rounded-full bg-primary" />
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            <div>
              <p className="text-text-muted text-[10px]">Откуда</p>
              <p className="text-text-primary text-sm font-medium truncate">{fromAddress.display_name.split(',')[0]}</p>
            </div>
            <div>
              <p className="text-text-muted text-[10px]">Куда</p>
              <p className="text-text-primary text-sm font-medium truncate">{toAddress.display_name.split(',')[0]}</p>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-accent-usdt text-xs font-medium">≈ {priceUsdt.toFixed(2)} USDT</p>
          </div>
        </div>

        {/* Rating UI - shown after ride is complete */}
        {status === 'complete' && !feedbackSubmitted && (
          <div className="mx-4 mb-3 p-4 bg-dark-secondary rounded-2xl border border-primary/10">
            <p className="text-white font-semibold text-sm text-center mb-3">Оцените поездку</p>
            <div className="flex justify-center gap-2 mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-xl transition-all ${
                    star <= rating
                      ? 'bg-amber-400/20 scale-110'
                      : 'bg-dark-tertiary hover:bg-dark-tertiary/80'
                  }`}
                >
                  <span className={star <= rating ? 'text-amber-400' : 'text-text-muted'}>
                    {star <= rating ? '\u2605' : '\u2606'}
                  </span>
                </button>
              ))}
            </div>
            {rating > 0 && (
              <>
                <input
                  type="text"
                  value={feedbackMsg}
                  onChange={(e) => setFeedbackMsg(e.target.value)}
                  placeholder="Комментарий (необязательно)"
                  className="w-full px-3 py-2 mb-3 bg-dark-tertiary border border-primary/10 rounded-xl text-sm text-white placeholder-text-muted focus:outline-none focus:border-primary/30"
                />
                <button
                  onClick={handleSubmitFeedback}
                  disabled={isSubmittingFeedback}
                  className="w-full py-2.5 rounded-xl font-semibold bg-gradient-primary text-white text-sm shadow-purple hover:shadow-purple-lg active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  {isSubmittingFeedback ? 'Отправка...' : 'Отправить оценку'}
                </button>
              </>
            )}
          </div>
        )}

        {status === 'complete' && feedbackSubmitted && (
          <div className="mx-4 mb-3 p-3 bg-accent-green/10 rounded-2xl border border-accent-green/20 text-center">
            <p className="text-accent-green text-sm font-medium">Спасибо за оценку!</p>
          </div>
        )}

        {/* Order ID */}
        <div className="mx-4 mb-2 flex items-center justify-between">
          <p className="text-text-muted text-xs">Заказ #{orderId.slice(0, 8)}</p>
          <p className="text-text-muted text-xs">{statusInfo.subtitle}</p>
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 pb-safe space-y-3">
          {canCancel && (
            <button
              onClick={handleCancel}
              disabled={isCancelling}
              className="w-full py-3.5 rounded-xl font-semibold bg-accent-red/10 text-accent-red border border-accent-red/20 hover:bg-accent-red/20 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {isCancelling ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Отмена...
                </span>
              ) : (
                'Отменить заказ'
              )}
            </button>
          )}

          {!isActive && (
            <button
              onClick={onNewOrder}
              className="w-full py-3.5 rounded-xl font-semibold bg-gradient-primary text-white shadow-purple hover:shadow-purple-lg active:scale-[0.98] transition-all"
            >
              Заказать ещё
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Declare Leaflet on window
declare global {
  interface Window {
    L: typeof import('leaflet')
  }
}
